import { Button } from '@thanx/uikit/button'
import { FormsyCheckbox } from '@thanx/uikit/checkbox'
import { FormsyInput } from '@thanx/uikit/input'
import { FormsyRadioGroup, Radio } from '@thanx/uikit/radio'
import { FormsyTextArea } from '@thanx/uikit/textarea'
import { defaultTheme, useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import ConfirmModal from 'components/ConfirmModal'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import PageHeader from 'components/PageHeader'
import Section from 'components/Section'
import Spinner from 'components/Spinner'
import Formsy from 'formsy-react'
import { useAlertBannerForm } from 'hooks/useAlertBannerForm'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import {
  formatPermissionReasonOr,
  usePermissionLabel,
  useRbacManageAnyDisabled,
  useRbacNavDisabled,
} from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import { AlertBanner } from 'models/Banner'
import React from 'react'
import { Container } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import { I18n } from 'react-redux-i18n'
import { Link, Redirect } from 'react-router-dom'

const t = buildTranslate('cms.content.alert_banner')

enum DismissType {
  YES = 'yes',
  NO = 'no',
}

type FormModel = {
  visibility: boolean
  title: string
  body: string
  buttonText: string
  buttonUrl: string
  dismissType: DismissType
}

const mapFromBanner = (banner: AlertBanner): FormModel => ({
  visibility: banner.enabled,
  title: banner.title || '',
  body: banner.body_text || '',
  buttonText: banner.button_text || '',
  buttonUrl: banner.button_url || '',
  dismissType: banner.dismissable ? DismissType.YES : DismissType.NO,
})

const mapToPayload = (
  form: FormModel,
  _banner: AlertBanner
): Partial<AlertBanner> => ({
  title: form.title,
  body_text: form.body,
  dismissable: form.dismissType === DismissType.YES,
  enabled: form.visibility,
  button_text: form.buttonText,
  button_url: form.buttonUrl,
})

type PublishBarProps = {
  disabled: boolean
  reason: string | undefined
  id: string
}

const PublishBar = ({ disabled, reason, id }: PublishBarProps) => (
  <div className="d-flex p-m align-items-center belize-hole-50 belize-hole-10-bkg border-radius-5">
    <p className="flex-grow-1 body-text-4 mr-m mb-0">
      <strong>{t('ready_to_publish')}</strong> {t('publish_changes')}
    </p>
    <PermissionTooltip
      disabled={disabled}
      reason={reason}
      id={id}
      placement="left"
      inline
    >
      <Button type="submit" className="flex-shrink-0" disabled={disabled}>
        {t('publish_settings')}
      </Button>
    </PermissionTooltip>
  </div>
)

const AlertBannerSettings = () => {
  const operationsCenterEnabled = useFlag('operations-center', false)
  const [css] = useStyletron()
  const dispatch = useDispatch()

  // Page-level access gate — must match the OC nav gate
  // (OrderingManagement OR CMS). Without this, direct URL navigation to
  // /alert_banners bypasses the nav-level RBAC check.
  const orderingMgmtDisabled = useRbacNavDisabled('OrderingManagement')
  const cmsDisabled = useRbacNavDisabled('CMS')
  const pageAccessDenied = orderingMgmtDisabled && cmsDisabled

  // Brand & Content is gated on any of CMS, AppConfiguration, DigitalExperiences
  // (matches the nav's Experiences/Content.tsx pattern). Governs the visual
  // styling cross-link tooltip on this page.
  const appConfigDisabled = useRbacNavDisabled('AppConfiguration')
  const digitalExpDisabled = useRbacNavDisabled('DigitalExperiences')
  const brandContentDisabled =
    cmsDisabled && appConfigDisabled && digitalExpDisabled

  const orderingMgmtLabel = usePermissionLabel('OrderingManagement')
  const cmsLabel = usePermissionLabel('CMS')
  const appConfigLabel = usePermissionLabel('AppConfiguration')
  const digitalExpLabel = usePermissionLabel('DigitalExperiences')
  const brandContentReason = formatPermissionReasonOr(
    [cmsLabel, appConfigLabel, digitalExpLabel],
    brandContentDisabled
  )

  const editDisabled = useRbacManageAnyDisabled(['OrderingManagement', 'CMS'])
  const editDisabledReason = formatPermissionReasonOr(
    [orderingMgmtLabel, cmsLabel],
    editDisabled
  )

  const {
    formModel,
    setFormModel,
    loadError,
    isSubmitting,
    isModalOpen,
    setIsModalOpen,
    publishChanges,
  } = useAlertBannerForm({
    mapFromBanner,
    mapToPayload,
    // Skip the GET when the page is about to redirect (flag off or RBAC
    // denied) so we don't fire a throwaway request for an unmounting
    // component.
    enabled: operationsCenterEnabled && !pageAccessDenied,
  })

  const pageTitle = I18n.t('nav.operations_center_menu.alert_banner_settings')

  // Full-sentence translation with a marker token so translators get the
  // whole clause in context — splitting the sentence into three keys blocks
  // languages that need different word order. Default the trailing fragment
  // to '' so an omitted {{link}} marker degrades gracefully (link renders at
  // the end of the sentence rather than producing `undefined` in the JSX).
  const [styleSentenceBefore, styleSentenceAfter = ''] = t(
    'edit_styling_sentence'
  ).split('{{link}}')

  // Flag off: the monolithic B&C alert banner form is the source of truth.
  // Redirect any direct hits on /alert_banners back there so URL navigation
  // can't stumble into the new page before Phase 1 is flipped on.
  if (!operationsCenterEnabled) {
    return <Redirect to="/brand_content#alert_banner" />
  }

  if (pageAccessDenied) {
    return <Redirect to="/unauthorized" />
  }

  // GET failed — render the shell with an error message instead of the
  // permanent spinner that `!formModel` would otherwise produce.
  if (loadError) {
    return (
      <PageHeader title={pageTitle}>
        <Helmet>
          <title>{I18n.t('titles./alert_banners')}</title>
        </Helmet>
        <Container className="px-0 pb-xl">
          <p className="mt-l body-text-3 grey-70" role="alert">
            {t('load_error')}
          </p>
        </Container>
      </PageHeader>
    )
  }

  // Render the page shell with a spinner while the initial fetch resolves
  // so the route doesn't flash a blank page.
  if (!formModel) {
    return (
      <PageHeader title={pageTitle}>
        <Helmet>
          <title>{I18n.t('titles./alert_banners')}</title>
        </Helmet>
        <Container className="px-0 pb-xl">
          <Spinner isLoading size="4x" className="text-center margin-top-huge">
            <></>
          </Spinner>
        </Container>
      </PageHeader>
    )
  }

  return (
    <Formsy
      onValidSubmit={() => {
        setIsModalOpen(true)
      }}
      onChange={newFormModel => {
        setFormModel(newFormModel)
      }}
      onInvalidSubmit={() =>
        dispatch(
          alert({
            key: 'danger',
            message: I18n.t('cms.content.form_error'),
            timeout: 5,
          })
        )
      }
    >
      <PageHeader
        title={pageTitle}
        description={
          <p className="mt-xs mb-m body-text-3">
            {t('settings_description')} {styleSentenceBefore}
            <PermissionTooltip
              disabled={brandContentDisabled}
              reason={brandContentReason}
              id="alert-banner-settings-brand-content-link"
              placement="top"
              inline
            >
              {brandContentDisabled ? (
                <span className="grey-50">{t('edit_styling_link')}</span>
              ) : (
                <Link to="/brand_content#alert_banner">
                  {t('edit_styling_link')}
                </Link>
              )}
            </PermissionTooltip>
            {styleSentenceAfter}
          </p>
        }
      >
        <Helmet>
          <title>{I18n.t('titles./alert_banners')}</title>
        </Helmet>
        <Container className="px-0 pb-xl">
          <PublishBar
            disabled={editDisabled}
            reason={editDisabledReason}
            id="alert-banner-settings-publish-top"
          />
          <Section className="p-m my-m">
            <p className="font-size-14 bold mb-m">{t('banner_visibility')}</p>
            {/* @ts-ignore */}
            <FormsyCheckbox
              name="visibility"
              value={formModel.visibility}
              disabled={editDisabled}
            >
              {t('make_visible')}
            </FormsyCheckbox>
            <p
              className={`
              ${css({
                marginLeft: '40px',
              })}
              body-text-5 grey-70 mb-xs mt-0`}
            >
              {t('visibility_description')}
            </p>
          </Section>
          <Section className="p-m mb-m">
            <p className="font-size-14 bold mb-m">{t('banner_content')}</p>
            <FormsyInput
              // @ts-ignore
              className="my-m"
              name="title"
              label={t('banner_title')}
              validations={{
                maxLength: 40,
              }}
              validationErrors={{
                maxLength: t('max_length_button'),
              }}
              value={formModel.title}
              disabled={editDisabled}
            />
            <FormsyTextArea
              // @ts-ignore
              className="my-xs"
              name="body"
              label={t('banner_body')}
              validations={{
                isRequired: true,
                maxLength: 130,
              }}
              validationErrors={{
                isRequired: t('body_required'),
                maxLength: t('max_length_body'),
              }}
              value={formModel.body}
              disabled={editDisabled}
              overrides={{
                Input: {
                  style: () => ({
                    minHeight: '64px',
                    fontSize: `14px`,
                    color: defaultTheme.colors.grey70,
                  }),
                },
              }}
            />
            <FormsyInput
              // @ts-ignore
              className="mb-m mt-m"
              name="buttonText"
              label={t('button_text')}
              validations={{
                maxLength: 40,
                areBothRequired: 'buttonUrl',
              }}
              validationErrors={{
                maxLength: t('max_length_button'),
                areBothRequired: I18n.t('validations.is_required'),
              }}
              value={formModel.buttonText}
              disabled={editDisabled}
            />
            <FormsyInput
              // @ts-ignore
              name="buttonUrl"
              label={t('button_url')}
              value={formModel.buttonUrl}
              disabled={editDisabled}
              validations={{
                isUrl: true,
                areBothRequired: 'buttonText',
              }}
              validationErrors={{
                isUrl: I18n.t('validations.is_url'),
                areBothRequired: I18n.t('validations.is_required'),
              }}
            />
          </Section>
          <Section className="p-m mb-m">
            <h4 className="mb-xxs">{t('allow_customers')}</h4>
            <p className="body-text-5 grey-70">{t('allow_description')}</p>
            <FormsyRadioGroup
              // @ts-ignore
              name="dismissType"
              align={'horizontal'}
              value={formModel.dismissType}
              disabled={editDisabled}
            >
              <Radio value={DismissType.YES}>{t('yes')}</Radio>
              <Radio value={DismissType.NO} className="ml-m">
                {t('no')}
              </Radio>
            </FormsyRadioGroup>
          </Section>
          <PublishBar
            disabled={editDisabled}
            reason={editDisabledReason}
            id="alert-banner-settings-publish-bottom"
          />
        </Container>
      </PageHeader>
      <ConfirmModal
        title={t('modal.publish_changes')}
        description={t('modal.you_are_publishing')}
        confirmText={t('modal.publish')}
        bannerText={t('modal.note')}
        isOpen={isModalOpen}
        isLoading={isSubmitting}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => publishChanges(formModel)}
      />
    </Formsy>
  )
}

export default AlertBannerSettings
